// PowerSync — агрегаты mastery по топикам через weak_spots + attempt_records.
//
// Для UI «Weak Spots» страницы. Mastery считается на клиенте через GROUP BY
// (агрегаты в стримах запрещены — фильтрация на клиенте через usePowerSyncQuery).
//
// LocalWeakSpot — РЕАЛЬНЫЕ колонки БД (information_schema): topic (не topic_key),
// total_questions (не questions_attempted), resolved (bool) + resolved_at,
// source_test_id/source_attempt_id (не test_id/attempt_id).
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { belongsToLiveMaterial } from "../live-material-sql";

export interface LocalWeakSpot {
	id: string;
	user_id: string;
	topic: string;
	accuracy: number;
	total_questions: number | null;
	resolved: number; // bool
	resolved_at: string | null;
	last_detected_at: string | null;
	material_id: string | null;
	questions_total: number | null;
	questions_wrong: number | null;
	confident_wrong: number | null;
	lucky_guesses: number | null;
	confidence_mismatch: number | null; // bool
	source_attempt_id: string | null;
	source_test_id: string | null;
	detection_count: number | null;
	resolved_by_attempt_id: string | null;
	severity: string | null;
	status: string | null;
	created_at: string;
}

/**
 * Пробел удалённого материала не показывается — ровно как в серверном
 * `weak-spots.list`. Каскада у `weak_spots` нет (в таблице нет `deleted_at`),
 * поэтому отбор — единственный способ; предикат общий, см. `live-material-sql`.
 */
const LIVE_MATERIAL = belongsToLiveMaterial("w");

/** Все weak_spots пользователя (включая resolved) — для list-страницы. */
export function useWeakSpotsQuery(userId: string | undefined) {
	return usePowerSyncQuery<LocalWeakSpot>(
		`SELECT w.* FROM weak_spots w
		   WHERE w.user_id = ? AND ${LIVE_MATERIAL}
		ORDER BY w.last_detected_at DESC, w.created_at DESC`,
		[userId ?? ""],
	);
}

/** Только активные (не resolved) — для mastery / dashboard. */
export function useActiveWeakSpotsQuery(userId: string | undefined) {
	return usePowerSyncQuery<LocalWeakSpot>(
		`SELECT w.* FROM weak_spots w
		   WHERE w.user_id = ? AND w.resolved_at IS NULL AND ${LIVE_MATERIAL}
		ORDER BY w.accuracy ASC`,
		[userId ?? ""],
	);
}

export function useWeakSpotsByAttemptQuery(attemptId: string | undefined) {
	return usePowerSyncQuery<LocalWeakSpot>(
		`SELECT w.* FROM weak_spots w
		   WHERE w.source_attempt_id = ? AND ${LIVE_MATERIAL}
		ORDER BY w.last_detected_at DESC`,
		[attemptId ?? ""],
	);
}

export function useTopicMasteryQuery(userId: string | undefined) {
	return usePowerSyncQuery<{
		topic: string;
		avg_accuracy: number;
		samples: number;
	}>(
		`SELECT w.topic AS topic,
		        AVG(w.accuracy) AS avg_accuracy,
		        COUNT(*) AS samples
		   FROM weak_spots w
		  WHERE w.user_id = ? AND ${LIVE_MATERIAL}
		GROUP BY w.topic
		ORDER BY avg_accuracy ASC`,
		[userId ?? ""],
	);
}
